// src/context/brandVoice/tabs.js — which tabs the Brand Voice screen shows, in which order.
//
// There is NO tab list in the API: the five document tabs are the `tab` values of
// GET /brand-identity/document's `fields[]`, and the voice tab is ours (the voice is
// a separate entity, not a field). The ORDER is a product decision (2026-09-27):
// Personalità del brand · Principi · Terminologia · Esempi di stile · Claim
// verificati · Messaging. ⭐ The five known document tabs are ALWAYS shown (even when
// the document failed to load — the tab then explains why), and a tab key the
// backend adds later is shown BEFORE Messaging with its raw key, never dropped.
export const VOICE_TAB = 'personality';
export const DOC_TABS = ['communication_principles', 'terminology', 'style_examples', 'verified_claims'];
export const LAST_TAB = 'messaging';
export const TAB_KEYS = [VOICE_TAB, ...DOC_TABS, LAST_TAB];

/**
 * @param {Array<{tab: string}>|null|undefined} fields  GET /document `fields[]` (or nothing)
 * @returns {Array<{ key: string, kind: 'voice'|'doc', known: boolean, fieldCount: number }>}
 */
export function buildTabs(fields) {
  const counts = new Map();
  (Array.isArray(fields) ? fields : []).forEach((f) => {
    if (f && typeof f.tab === 'string' && f.tab) counts.set(f.tab, (counts.get(f.tab) || 0) + 1);
  });
  const known = new Set([...DOC_TABS, LAST_TAB]);
  const unknown = [...counts.keys()].filter((k) => !known.has(k));
  const doc = (key, isKnown) => ({ key, kind: 'doc', known: isKnown, fieldCount: counts.get(key) || 0 });
  return [
    { key: VOICE_TAB, kind: 'voice', known: true, fieldCount: 0 },
    ...DOC_TABS.map((k) => doc(k, true)),
    ...unknown.map((k) => doc(k, false)),
    doc(LAST_TAB, true),
  ];
}

/** v200: the i18n key of each known tab's one-line intro, drawn in the row it shares with the "Avanzate" toggle. */
export const TAB_INTRO_KEYS = {
  [VOICE_TAB]: 'brandVoice.personalityIntro',
  communication_principles: 'brandVoice.principlesIntro',
  terminology: 'brandVoice.terminologyIntro',
  style_examples: 'brandVoice.styleExamplesIntro',
  verified_claims: 'brandVoice.verifiedClaimsIntro',
  [LAST_TAB]: 'brandVoice.messagingIntro',
};

/** The intro key for a tab from `buildTabs`; a tab the backend added later says it is not handled yet. */
export function tabIntroKey(tab) {
  if (!tab) return null;
  return TAB_INTRO_KEYS[tab.key] || (tab.known ? null : 'brandVoice.unknownTab');
}
